import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { adminService, AdminStats } from '../../services/adminService';
import { User } from '../../types';

// Define the initial state interface
interface AdminState {
  users: User[];
  pendingUsers: User[];
  selectedUser: User | null;
  stats: AdminStats | null;
  isLoading: boolean;
  isApproving: boolean;
  isRejecting: boolean;
  isRevoking: boolean;
  isFetchingStats: boolean;
  error: string | null;
}

// Define the initial state
const initialState: AdminState = {
  users: [],
  pendingUsers: [],
  selectedUser: null,
  stats: null,
  isLoading: false,
  isApproving: false,
  isRejecting: false,
  isRevoking: false,
  isFetchingStats: false,
  error: null,
};

// Async thunks
export const fetchAllUsers = createAsyncThunk(
  'admin/fetchAllUsers',
  async (_, { rejectWithValue }) => {
    try {
      const users = await adminService.getAllUsers();
      return users;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch users');
    }
  }
);

export const fetchPendingUsers = createAsyncThunk(
  'admin/fetchPendingUsers',
  async (_, { rejectWithValue }) => {
    try {
      const pendingUsers = await adminService.getPendingUsers();
      return pendingUsers;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch pending users');
    }
  }
);

export const fetchAdminStats = createAsyncThunk(
  'admin/fetchAdminStats',
  async (_, { rejectWithValue }) => {
    try {
      const stats = await adminService.getAdminStats();
      return stats;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch admin statistics');
    }
  }
);

export const approveUser = createAsyncThunk(
  'admin/approveUser',
  async (userId: string, { rejectWithValue, dispatch }) => {
    try {
      await adminService.approveUser(userId);
      // Refresh data
      dispatch(fetchAllUsers());
      dispatch(fetchPendingUsers());
      dispatch(fetchAdminStats());
      return userId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to approve user');
    }
  }
);

export const rejectUser = createAsyncThunk(
  'admin/rejectUser',
  async (userId: string, { rejectWithValue, dispatch }) => {
    try {
      await adminService.rejectUser(userId);
      // Refresh data
      dispatch(fetchAllUsers());
      dispatch(fetchPendingUsers());
      dispatch(fetchAdminStats());
      return userId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to reject user');
    }
  }
);

export const revokeUser = createAsyncThunk(
  'admin/revokeUser',
  async (userId: string, { rejectWithValue, dispatch }) => {
    try {
      await adminService.revokeUserApproval(userId);
      // Refresh data
      dispatch(fetchAllUsers());
      dispatch(fetchPendingUsers());
      dispatch(fetchAdminStats());
      return userId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to revoke user');
    }
  }
);

// Create the admin slice
const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all users
      .addCase(fetchAllUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload;
        state.error = null;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch pending users
      .addCase(fetchPendingUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPendingUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pendingUsers = action.payload;
        state.error = null;
      })
      .addCase(fetchPendingUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch admin stats
      .addCase(fetchAdminStats.pending, (state) => {
        state.isFetchingStats = true;
        state.error = null;
      })
      .addCase(fetchAdminStats.fulfilled, (state, action) => {
        state.isFetchingStats = false;
        state.stats = action.payload;
        state.error = null;
      })
      .addCase(fetchAdminStats.rejected, (state, action) => {
        state.isFetchingStats = false;
        state.error = action.payload as string;
      })
      // Approve user
      .addCase(approveUser.pending, (state) => {
        state.isApproving = true;
        state.error = null;
      })
      .addCase(approveUser.fulfilled, (state) => {
        state.isApproving = false;
        state.error = null;
      })
      .addCase(approveUser.rejected, (state, action) => {
        state.isApproving = false;
        state.error = action.payload as string;
      })
      // Reject user
      .addCase(rejectUser.pending, (state) => {
        state.isRejecting = true;
        state.error = null;
      })
      .addCase(rejectUser.fulfilled, (state) => {
        state.isRejecting = false;
        state.error = null;
      })
      .addCase(rejectUser.rejected, (state, action) => {
        state.isRejecting = false;
        state.error = action.payload as string;
      })
      // Revoke user
      .addCase(revokeUser.pending, (state) => {
        state.isRevoking = true;
        state.error = null;
      })
      .addCase(revokeUser.fulfilled, (state) => {
        state.isRevoking = false;
        state.error = null;
      })
      .addCase(revokeUser.rejected, (state, action) => {
        state.isRevoking = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const { clearError, setSelectedUser, clearSelectedUser } = adminSlice.actions;

// Export selectors
export const selectAllUsers = (state: any) => state.admin.users;
export const selectPendingUsers = (state: any) => state.admin.pendingUsers;
export const selectSelectedUser = (state: any) => state.admin.selectedUser;
export const selectAdminStats = (state: any) => state.admin.stats;
export const selectAdminLoading = (state: any) => state.admin.isLoading;
export const selectAdminError = (state: any) => state.admin.error;
export const selectAdminApproving = (state: any) => state.admin.isApproving;
export const selectAdminRejecting = (state: any) => state.admin.isRejecting;
export const selectAdminRevoking = (state: any) => state.admin.isRevoking;
export const selectAdminStatsLoading = (state: any) => state.admin.isFetchingStats;

// Export reducer
export default adminSlice.reducer;
